import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Settings2, RefreshCw, LoaderCircle } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import { setSettingsModalOpen, setSettingsInitialTab } from '../../store/uiModalStore';
import TerminalLaunchList from './TerminalLaunchList';
import { useVisibleTerminalShells } from './useTerminalShells';
import { launchProfile } from './launchProfile';
import './TerminalMenu.css';

/**
 * The tab bar's "+" menu: AGENTS (profiles, with install detection), SHELLS
 * (`terminal_list_shells`) and "SSH connection…", then Manage profiles and
 * Re-check. Click = new tab; Alt+click = split right of the focused pane;
 * Alt+Shift+click = split below (each agent row's tooltip says so, and the
 * row's hover buttons do the same; its folder button opens a new tab and asks
 * for the working directory even when a default folder is set). Keyboard: type to filter, ↑/↓ to move,
 * Enter to open (Alt / Alt+Shift as for clicks), Esc to close. Detection is
 * async and cached — rows render at once with a spinner in the status slot.
 * The list itself is `TerminalLaunchList` (shared with "Split with…").
 */
export default function TerminalNewMenu({ onOpenLocal, onOpenSsh }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const shells = useVisibleTerminalShells(); // minus the ones hidden in Settings
  const detecting = useTerminalConfigStore((s) => s.detecting);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const whereOf = (e) => (e?.altKey ? (e.shiftKey ? 'col' : 'row') : 'tab');
  const close = () => setOpen(false);

  const manage = () => {
    setOpen(false);
    setSettingsInitialTab('extensions:com.apiboo.terminal');
    setSettingsModalOpen(true);
  };

  const footer = (
    <div className="term-menu-footer">
      <button type="button" className="term-menu-foot-btn" onClick={manage}>
        <Settings2 size={13} aria-hidden />
        {t('terminalExt.manageProfiles', 'Manage profiles…')}
      </button>
      <button
        type="button"
        className="term-menu-foot-btn"
        onClick={() => useTerminalConfigStore.getState().detect(true)}
        disabled={detecting}
      >
        {detecting ? <LoaderCircle size={13} className="animate-spin" aria-hidden /> : <RefreshCw size={13} aria-hidden />}
        {t('terminalExt.recheck', 'Re-check')}
      </button>
    </div>
  );

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <Tooltip content={t('terminalExt.newTerminal', 'New terminal')} side="bottom">
        <button
          type="button"
          className="flex items-center px-2 py-1 flex-shrink-0 cursor-pointer transition-colors duration-150 etm-tab etm-tab--inactive"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="menu"
        >
          <Plus size={14} className="flex-shrink-0" />
          {/* invisible spacer — same height as a tab with a close button */}
          <span
            aria-hidden="true"
            style={{ width: 0, display: 'inline-flex', alignItems: 'center', padding: '4px 0', overflow: 'hidden', flexShrink: 0 }}
          >
            <X size={11} />
          </span>
        </button>
      </Tooltip>
      {open && (
        <TerminalLaunchList
          className="term-menu absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
          shells={shells}
          showSsh
          agentSplitActions
          onAgent={(p, e, where) => {
            close();
            if (where === 'folder') launchProfile(p, 'tab', {}, { pickCwd: true });
            else launchProfile(p, where || whereOf(e));
          }}
          onShell={(shell) => {
            close();
            onOpenLocal(shell);
          }}
          onSsh={() => {
            close();
            onOpenSsh();
          }}
          onClose={close}
          footer={footer}
        />
      )}
    </div>
  );
}
